import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";

interface MetadataPreviewProps {
  metadata: Record<string, any>;
  onExport?: () => void;
}

export function MetadataPreview({ metadata, onExport }: MetadataPreviewProps) {
  const copyToClipboard = () => {
    const text = JSON.stringify(metadata, null, 2);
    navigator.clipboard.writeText(text);
    toast.success("Metadata copied to clipboard");
  };

  return (
    <Card className="w-full max-w-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Generated Metadata</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
            className="gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy
          </Button>
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {metadata.title && (
          <div className="border-b border-border pb-4">
            <p className="text-sm text-muted-foreground mb-1">Track Title</p>
            <p className="text-lg font-semibold">{metadata.title}</p>
          </div>
        )}

        {metadata.artist && (
          <div className="border-b border-border pb-4">
            <p className="text-sm text-muted-foreground mb-1">Artist</p>
            <p className="text-lg font-semibold">{metadata.artist}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {metadata.album && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Album</p>
              <p className="font-medium">{metadata.album}</p>
            </div>
          )}

          {metadata.genre && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Genre</p>
              <Badge variant="secondary">{metadata.genre}</Badge>
            </div>
          )}

          {metadata.releaseDate && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Release Date</p>
              <p className="font-medium">
                {new Date(metadata.releaseDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {metadata.isrc && (
          <div className="bg-muted/50 p-4 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-1">ISRC Code</p>
            <p className="font-mono font-semibold text-lg">{metadata.isrc}</p>
          </div>
        )}

        {metadata.iswc && (
          <div className="flex gap-4">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-1">ISWC</p>
              <p className="font-mono">{metadata.iswc}</p>
            </div>
            {metadata.ipi && (
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">IPI</p>
                <p className="font-mono">{metadata.ipi}</p>
              </div>
            )}
          </div>
        )}

        {metadata.contributors && (
          <div className="bg-muted/50 p-4 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground mb-2">Contributors</p>
            <p className="text-sm leading-relaxed">{metadata.contributors}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
